/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import classNames from 'classnames';
import { Button, Collapse, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';
import PoslumaNavBar from '../../../micro/BrandNavBar/PoslumaNavBar';
import TrainingNavBar from '../../../micro/TrainingNavBar/PoslumaTrainingNavBar';
import { BASE_URL, DOCUMENT_PATHS, IMAGE_PATHS } from '../../../../constants/paths.constants';
import {
  poslumaBox,
  poslumaBlue,
  white,
  poslumaDarkGrey,
  disabledGrey,
  dividerGrey,
  buttonText,
  poslumaTheme,
  universalStyles,
} from '../../../../app/theme';
import Indication from '../../../micro/Indication/PoslumaIndication';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import { TRAINING_NAVIGATION, BASE_ROUTE } from '../../../../constants/trainingNav.constants';
import history from '../../../../services/browserHistory';
import AppState from '../../../../models/redux/state/app.state';
import { getTrainingAPIcall } from '../../../../redux/actions/training/training.actions';
import SingleTraining from '../../../../models/training/SingleTraining';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  physicianImage: {
    display: 'block',
    width: '100%',
    maxWidth: '100vw',
    height: 'auto',
  },
  grayBox: {
    backgroundColor: poslumaDarkGrey,
    width: '100%',
    margin: 0,
    paddingBottom: 56,
  },
  h1: {
    padding: '37px 0 0 64px',
    margin: 0,
  },
  subheader: {
    padding: '10px 0 0 64px',
    margin: 0,
    fontSize: 20,
  },
  poslumaBox: {
    backgroundColor: poslumaBox,
    width: 'calc(100% - 128px)',
    boxSizing: 'border-box',
    margin: '56px 64px 0',
    paddingTop: 21,
    paddingBottom: 40,
    paddingLeft: 43,
    paddingRight: 31,
  },
  headerRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleText: {
    fontWeight: 900,
    color: white,
    fontSize: 22,
    marginLeft: -25,
    cursor: 'pointer',
    '&::before': {
      content: '"+"',
      width: '1em',
      display: 'inline-block',
      color: poslumaBlue,
      fontWeight: 600,
    },
  },
  expanded: {
    '&::before': {
      content: '"-"',
    },
  },
  disabledText: {
    color: disabledGrey,
    cursor: 'default',
    '&::before': {
      color: disabledGrey,
    },
  },
  enterButton: {
    width: 174,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 24,
    color: buttonText,
    '&.Mui-disabled': {
      backgroundColor: dividerGrey,
    },
  },
  subHeaderRow: {
    marginTop: 8,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  subHeaderItem: {
    width: '25%',
    color: white,
    fontSize: 18,
  },
  h2: {
    fontSize: 20,
    color: white,
    fontWeight: 900,
    marginTop: 28,
    marginBottom: 10,
  },
  bodyText: {
    color: white,
    fontSize: 18,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    color: white,
    fontSize: 18,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      paddingRight: 15,
      display: 'block',
      color: white,
      fontWeight: 600,
    },
  },
  nestedList: {
    listStyle: 'none',
    margin: 0,
    padding: '0 0 0 30px',
    fontSize: 18,
    color: white,
    lineHeight: 1.3,
    '& > li': {
      padding: '8px 0',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2013"',
      color: white,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
}));

const PoslumaTrainingOverview = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { location } = history;
  const [isTrainingExpanded, setIsTrainingExpanded] = useState(true);
  const [isCaseStudiesExpanded, setIsCaseStudiesExpanded] = useState(false);
  // const [isLCExpanded, setIsLCExpanded] = useState(false);
  const [isStatementExpanded, setIsStatementExpanded] = useState(false);
  const trainingInfo = useSelector((state: AppState) => state.training.current);
  const [currentTraining, setCurrentTraining] = useState<SingleTraining>();
  const [module, setModule] = useState('');
  const [displayModule, setDisplayModule] = useState('');

  useEffect(() => {
    const segments = location.pathname.split('/');
    const newModule = segments[segments.length - 1];
    const newDisplayModule = newModule[0].toUpperCase() + newModule.substring(1);
    setModule(`posluma${newDisplayModule}`);
    setDisplayModule(newDisplayModule);
    document.title = `Welcome to POSLUMA ${newDisplayModule} Training`;
    setIsTrainingExpanded(false);
    setIsCaseStudiesExpanded(false);
    // setIsLCExpanded(false);
    setIsStatementExpanded(false);
  }, [location]);

  useEffect(() => {
    dispatch(getTrainingAPIcall('2'));
  }, [dispatch]);

  useEffect(() => {
    if (trainingInfo && module) {
      const match = trainingInfo.trainings.find((training: { training: { name: string } }) => {
        // These IDs (3 and 4) should probably be saved in a constants file
        // Even better would be to make the API call to get it?
        const idMatch = module === 'poslumaInterpretation' ? 'Interpretation' : 'Acquisition';
        return idMatch === training.training.name;
      });
      if (!match && currentTraining) {
        history.push('/trainings');
      }
      setCurrentTraining(match);
    }
  }, [trainingInfo, module, currentTraining]);

  const handleClickEnter = () => {
    let baseRoute;
    let navigationData;
    if (module === 'poslumaInterpretation') {
      baseRoute = BASE_ROUTE.poslumaInterpretation;
      navigationData = TRAINING_NAVIGATION.poslumaInterpretation;
    } else {
      baseRoute = BASE_ROUTE.poslumaAcquisition;
      navigationData = TRAINING_NAVIGATION.poslumaAcquisition;
    }
    const page = currentTraining?.currentPage && !currentTraining?.dateCompleted ? currentTraining.currentPage : 1;
    const { route } = navigationData[page];
    history.push(`${baseRoute}${route}`);
  };
  const caseStudiesExpandHandler = () => {
    if (currentTraining && currentTraining.dateCompleted) {
      setIsCaseStudiesExpanded(!isCaseStudiesExpanded);
    }
  };
  // const LCExpandHandler = () => {
  //   if (currentTraining && currentTraining.dateCompleted) {
  //     setIsLCExpanded(!isLCExpanded);
  //   }
  // };
  const statementExpandHandler = () => {
    if (currentTraining && currentTraining.dateCompleted) {
      setIsStatementExpanded(!isStatementExpanded);
    }
  };
  const handleClickDownload = () => {
    const action =
      module === 'poslumaInterpretation' ? 'POSLUMA INT Stmt of Completion' : 'POSLUMA ACQ Stmt of Completion';
    ReactGA.event({
      category: 'event',
      action,
    });
  };

  return (
    <MuiThemeProvider theme={poslumaTheme}>
      <TrainingNavBar />
      <PoslumaNavBar section={`Image ${displayModule}`} />
      <img
        className={classes.physicianImage}
        src={`${BASE_URL}${
          module === 'poslumaInterpretation'
            ? IMAGE_PATHS.TRAINING_OVERVIEW[2].PHYSICIANS.path
            : IMAGE_PATHS.TRAINING_OVERVIEW[2].TECHNOLOGISTS.path
        }`}
        alt={`${
          module === 'poslumaInterpretation'
            ? IMAGE_PATHS.TRAINING_OVERVIEW[2].PHYSICIANS.alt
            : IMAGE_PATHS.TRAINING_OVERVIEW[2].TECHNOLOGISTS.alt
        }`}
      />
      <div className={classes.grayBox}>
        <Typography variant="h1" className={classes.h1}>
          {`Image ${displayModule} Training`}
        </Typography>
        <Typography className={classes.subheader} variant="h6">
          {`Upon completion of the recommended Image ${displayModule} Training, 
          clinical case studies will be available.`}
        </Typography>
        <div className={classes.poslumaBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(classes.titleText, isTrainingExpanded ? classes.expanded : '')}
              onClick={() => setIsTrainingExpanded(!isTrainingExpanded)}
            >
              Recommended Training
            </Typography>
            <Button variant="contained" color="primary" className={classes.enterButton} onClick={handleClickEnter}>
              Enter
            </Button>
          </div>
          <Collapse in={isTrainingExpanded} timeout="auto" unmountOnExit>
            <div className={classes.subHeaderRow}>
              <span className={classes.subHeaderItem}>
                <b>Time to Complete: </b>
                <span>{module === 'poslumaInterpretation' ? '60 minutes' : '30 minutes'}</span>
              </span>
              <span className={classes.subHeaderItem}>
                <b>Date Added: </b>
                <span>June 2, 2023</span>
              </span>
              <span className={classes.subHeaderItem}>
                <b>Progress: </b>
                {currentTraining && currentTraining.status.name}
              </span>
            </div>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {module === 'poslumaInterpretation'
                ? 'This training is designed to help nuclear medicine physicians understand the recommended image interpretation criteria for POSLUMA.'
                : 'This training is designed for nuclear medicine technologists to understand the dosing and administration of POSLUMA as well as to optimize the image acquisition protocol.'}
            </Typography>
            <Typography className={classes.h2}>Learning Objectives:</Typography>
            <Typography className={classes.bodyText}>
              Following completion of this training, you should be able to
            </Typography>
            <ul className={classes.list}>
              <li>
                Describe the biodistribution of POSLUMA and use the knowledge during PET image&nbsp;
                {module === 'poslumaInterpretation' ? 'interpretation' : 'acquisition'}
              </li>
              <li>Illustrate key technical aspects of the practical use of POSLUMA with respect to the following</li>
              <ul className={classes.nestedList}>
                <li>Image instrumentation</li>
                <li>Patient preparation</li>
                <li>POSLUMA dosage and administration</li>
                <li>Image acquisition and reconstruction</li>
              </ul>
              {module === 'poslumaInterpretation' && (
                <>
                  <li>Describe the recommended image interpretation criteria for POSLUMA</li>
                  <li>
                    Apply learnings from the recommended image interpretation training to patients who have undergone
                    PET scanning with POSLUMA
                  </li>
                </>
              )}
            </ul>
          </Collapse>
        </div>
        <div className={classes.poslumaBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(
                classes.titleText,
                currentTraining && currentTraining.dateCompleted ? ' ' : classes.disabledText,
                isCaseStudiesExpanded ? classes.expanded : '',
              )}
              onClick={caseStudiesExpandHandler}
            >
              Additional Cases
            </Typography>
            <Button
              variant="contained"
              disabled={!currentTraining || !currentTraining.dateCompleted}
              color="primary"
              className={classes.enterButton}
              href="/posluma/casestudies"
            >
              Enter
            </Button>
          </div>
          <Collapse in={isCaseStudiesExpanded} timeout="auto" unmountOnExit>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {`This section includes clinical cases that provide example images and information available following
              POSLUMA PET imaging. Upon completion of the training program, additional case studies will be available to give nuclear medicine ${
                module === 'poslumaInterpretation' ? 'physicians' : 'technologists'
              } a better
              understanding of their responsibility to deliver accurate ${
                module === 'poslumaInterpretation'
                  ? ' interpretation POSLUMA PET images.'
                  : ' and timely acquisition of images using POSLUMA PET scanning.'
              }`}
            </Typography>
          </Collapse>
        </div>
        <div className={classes.poslumaBox}>
          <div className={classes.headerRow}>
            <Typography
              className={classNames(
                classes.titleText,
                currentTraining && currentTraining.dateCompleted ? ' ' : classes.disabledText,
                isStatementExpanded ? classes.expanded : '',
              )}
              onClick={statementExpandHandler}
            >
              {`Image ${displayModule} Statement of Completion`}
            </Typography>
            <a
              href={`${BASE_URL}${
                module === 'poslumaInterpretation'
                  ? DOCUMENT_PATHS[2].ACQUISITION_COMPLETE.path
                  : DOCUMENT_PATHS[2].INTERPRETATION_COMPLETE.path
              }`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="contained"
                disabled={!currentTraining || !currentTraining.dateCompleted}
                color="primary"
                className={classes.enterButton}
                onClick={handleClickDownload}
              >
                Download
              </Button>
            </a>
          </div>
          <Collapse in={isStatementExpanded} timeout="auto" unmountOnExit>
            <Typography className={classes.h2}>Description:</Typography>
            <Typography className={classes.bodyText}>
              {`Upon conclusion of the recommended Image ${displayModule} Training, you can download a statement of completion.`}
            </Typography>
          </Collapse>
        </div>
      </div>
      <Indication />
      <TrainingFooter />
    </MuiThemeProvider>
  );
};

export default PoslumaTrainingOverview;
